import {
  createAsyncThunk,
  createSelector,
  createSlice,
  Dispatch,
  PayloadAction,
} from '@reduxjs/toolkit';
import { WorkflowRunState } from '@smartsheet-bridge/bridge-sdk';
import {
  BridgeHTTPQuery,
  BridgeHTTPQueryResponse,
} from '@smartsheet-bridge/bridge-sdk/es/http/createQuery';
import GraphJS, { ALIGN_CENTER } from '@smartsheet/graphjs';
import { OptionType } from '@smartsheet/lodestar-core';
import _, { omit, transform, without } from 'lodash';
import { compose } from 'ramda';
import { generateFourDigitNumber } from '../../../components/features/Canvas/Nodes/utils';
import { isLabelUnique } from '../../../components/features/RightRail/EditModule/utils';
import { triggerParser } from '../../../utils/graphjs/parsers/triggerParser';
import {
  parseWorkflow,
  switchBackToLegacyViewWithError,
} from '../../../utils/helpers';
import { toNanoseconds } from '../../../utils/time';
import { uuid } from '../../../utils/uuid';
import { Module } from '../../api/ExtensionService/ExtensionService';
import { getWorkflow, saveWorkflow, Workflow } from '../../api/WorkflowService';
import api from '../../api/WorkflowService/WorkflowService';
import {
  AllModuleParamModel,
  ModuleModel,
} from '../../forms/module/ModuleModelTypes';
import { store, StoreState } from '../../store/store';
import {
  ExistingGraphNodeItem,
  GraphNodeTypes,
  NewGraphNodeItem,
} from '../designerDuck/designerDuck';
import { selectModuleById } from '../modulesDuck/modulesDuck';

const SESSION_WORKFLOW_KEY = 'session_workflow';

export interface WorkflowRunFilterOption extends OptionType {
  label: string;
  value: string;
  states: string[];
}

export enum UIWorkflowRunState {
  'IN_PROGRESS' = 0,
  'WAITING' = 100,
  'WAITING_ON_CHATBOT_REPLY' = 110,
  'STOPPED' = 200,
  'STOPPED_BY_ERROR' = 210,
  'DONE' = 300,
}

export type StartNode = string;
export type WorkflowStateNode = {
  nextState?: string[];
  groupExitID?: string;
  id: string;
  type: GraphNodeTypes;
  moduleID?: string;
  pluginUUID?: string;
  next: string[];
  state?: string;
  childConversation?: string;
  childIterator?: string | number;
  childInput?: any;
  moduleData?: ModuleModel<AllModuleParamModel>;
  question?: string;
  retryHelpTxt?: string;
  isJunctionState?: boolean;
  runModules?: { [key: string]: any }[];
  groupName?: string;
  exit?: string;
};

export interface WorkflowState {
  response: Workflow;
  data: {
    start: StartNode;
    nodes: Record<string, WorkflowStateNode>;
    graph: typeof GraphJS;
  };
  runsById: {
    [objectUUID: string]: WorkflowRun;
  };
  visibleRunIds: string[];
  filter: WorkflowRunFilterOption;
  fromRun: number;
  totalRuns: number;
  runsWereFound: boolean;
  userSetFromTimestamp: string;
  isLoading: boolean;
  isSaving: boolean;

  hasChanged: boolean;
  originalNodes: Record<string, WorkflowStateNode>;

  hasNetworkError: boolean;
  defaultFromTimestamp: string;
  fetchedAtleastOnce: boolean;
}

export interface WorkflowRun {
  objectUUID: string;
  createTime: number;
  createTimeString: string;
  lastUpdateTime: number;
  lastUpdateTimeString: string;
  serviceOwner: string;
  keys?: any;
  orderBy?: any;
  delete?: any;
  intent: any;
  startState?: any;
  domain?: any;
  isSystem?: any;
  transits?: any;
  answer?: any;
  workspace: string;
  transient?: any;
  requestUUID: string;
  state: WorkflowRunState;
  handlerUUID?: string;
  currentTransit: any;
  expiryTime?: number;
  failedParse: any;
  activeTime?: number;
  extra?: any;
  useruuid: string;
  ParentConversation?: any;
  parentData?: any;
}

const initialState: WorkflowState = {
  data: {
    start: '',
    nodes: {},
    graph: {},
  },
  response: {
    intent: '',
    objectUUID: '',
  },
  filter: {
    label: 'Filter',
    value: 'all',
    states: ['0', '1', '2', '3', '4', '5'],
  } as WorkflowRunFilterOption,
  fromRun: 0,
  totalRuns: 0,
  runsWereFound: false,
  runsById: {},
  visibleRunIds: [],
  userSetFromTimestamp: '',
  isLoading: false,
  isSaving: false,
  hasChanged: false,
  originalNodes: {},
  hasNetworkError: false,
  defaultFromTimestamp: '',
  fetchedAtleastOnce: false,
};

export const MAX_RECORDS = 22;

const fixGroupName = (workflowData: any) => {
  if (workflowData.transits) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(workflowData.transits).forEach(([_, state]: any) => {
      state.groupName = state.groupName || '';
      if (state.runModules) {
        state.runModules.forEach((runModule: any) => {
          if (!runModule.groupName) {
            runModule.groupName = state.groupName;
          }
        });
      }
    });
  }
  return workflowData;
};

export const fetchWorkflowData = createAsyncThunk<
  Workflow,
  string,
  {
    state: StoreState;
  }
>('workflow/fetchWorkflowData', async workflowUUID => {
  const sessionWorkflow: any = sessionStorage.getItem(SESSION_WORKFLOW_KEY);
  if (sessionWorkflow) {
    try {
      const workflowData: any = JSON.parse(sessionWorkflow);
      if (workflowData.startState && workflowData.transits) {
        if (
          workflowUUID &&
          workflowUUID.trim() &&
          workflowData.intent === workflowUUID
        ) {
          return {
            ...fixGroupName(workflowData),
            isModified: true,
          };
        }
      }
    } catch (err) {
      // TODO Handle this error properly - ideally we should show a toast to the user without
      // removing the sessionWorkflow from the sessionStorage until the user acknowledges the toast
      // or the toast disappears. (Note: we automatically remove the workflow from sessionStorage after a successful parseWorkflow())
      console.error(
        'Failed to process the workflow passed from the old view.',
        err
      );
      throw err;
    }
  }

  const data = await getWorkflow(workflowUUID);
  return {
    ...data,
    isModified: false,
  };
});

export const fetchWorkflowRunsList = createAsyncThunk<
  BridgeHTTPQueryResponse<WorkflowRun>,
  undefined,
  {
    state: StoreState;
  }
>('workflow/fetchWorkflowRunsList', async (_, { getState }) => {
  const state = getState();
  const fromTimestamp =
    userSetFromTimestampSelector(state) || defaultFromTimestampSelector(state);
  const fromRun = fromRunSelector(state);
  const filter = filterSelector(state);
  const workflowIntent = workflowNameSelector(state);

  const queryFilter: BridgeHTTPQuery = {
    fromRecord: fromRun,
    maxRecords: MAX_RECORDS,
    page: 0,
    filters: [
      { field: 'intent', value: [workflowIntent], condition: 'IS_EQUAL_TO' },
      {
        field: 'createTime',
        value: [toNanoseconds(fromTimestamp)],
        condition: 'IS_LESS_THAN_EQUAL',
      },
      {
        field: 'state',
        value: filter.states,
        condition: 'IS_IN',
      },
    ],
    orderBy: [
      {
        field: 'createTime',
        ascending: false,
      },
    ],
  };
  const { data } = await api.workflowRun.query(queryFilter, {
    params: {
      metadata: true,
    },
  });
  return data;
});

const getWorkflowDataFromState = (state: WorkflowState): Workflow => {
  const stateNodes = state.data.nodes;
  const transits = createTransitMap(stateNodes);
  const startStateNode: any = Object.values(stateNodes).find(
    (node: any) => node.id === state.data.start
  );
  const workflowData: Workflow = {
    ...state.response,
    startState: startStateNode
      ? startStateNode.state
      : state.response.startState,
    transits: transits,
  };
  return workflowData;
};

export const saveWorkflowData = createAsyncThunk<
  any,
  void,
  { state: StoreState }
>('workflow/saveWorfklowData', async (_, { getState }) => {
  const state = getState();
  const workflowData = getWorkflowDataFromState(state.workflow);
  const data = await saveWorkflow(workflowData);
  return data;
});

export const switchToLegacyView = createAsyncThunk<
  void,
  void,
  { state: StoreState }
>('workflow/switchToLegacyView', async (_, { getState }: any) => {
  const state = getState();
  const hasChanged = hasChangedSelector(state);
  if (hasChanged) {
    const workflowData = getWorkflowDataFromState(state.workflow);
    const payload = JSON.stringify(workflowData);
    sessionStorage.setItem(SESSION_WORKFLOW_KEY, payload);
  }
  window.location.href = `${window.location.origin}#${window.location.pathname}`;
});

export type insertNodePayload = {
  type?: GraphNodeTypes;
  payload: {
    pluginUUID?: string;
    moduleID?: string;
    parentID: string;
    name: string;
  };
};

export const moveNode =
  (args: {
    parentID: string;
    name: string;
    nodeToInsert: ExistingGraphNodeItem | NewGraphNodeItem;
    module?: Module;
  }) =>
  (dispatch: Dispatch, getState: typeof store.getState) => {
    const state = getState();
    const nodes = state.workflow.data.nodes;
    const upwardsMap = state.workflow.data.graph.upwardsMap;
    dispatch(
      removeNodeFromGraph({
        id: args.nodeToInsert.id,
        type: args.nodeToInsert.type,
      })
    );
    dispatch(insertNodeIntoGraph(args));
    if (args.nodeToInsert.type === GraphNodeTypes.STATE) {
      let shouldBreak = false;
      traverseGraph(
        nodes,
        args.nodeToInsert.id,
        (result, node) => {
          if (!shouldBreak && node.type === GraphNodeTypes.MODULE) {
            dispatch(
              insertNodeIntoGraph({
                parentID: upwardsMap[node.id],
                nodeToInsert: {
                  ...node,
                  __type: 'existing',
                  type: GraphNodeTypes.MODULE,
                },
                module: selectModuleById(
                  node.moduleID ?? '',
                  node.pluginUUID ?? ''
                )(state),
                name: 'New State',
              })
            );
          } else {
            shouldBreak = true;
          }
        },
        true
      );
    }
  };

const WorkflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<WorkflowRunFilterOption>) => {
      state.fromRun = 0;
      state.totalRuns = 0;
      state.filter = action.payload;
      state.visibleRunIds = [];
      state.runsById = {};
      state.fetchedAtleastOnce = false;
    },
    setFromRun: (state, action) => {
      state.fromRun = action.payload;
    },
    setUserSetTimestamp: (state, action) => {
      state.userSetFromTimestamp = action.payload;
      state.defaultFromTimestamp = '';
      state.visibleRunIds = [];
      state.runsById = {};
      state.fromRun = 0;
      state.totalRuns = 0;
      state.fetchedAtleastOnce = false;
    },
    setDefaultFromTimestamp: (state, action) => {
      state.defaultFromTimestamp = action.payload;
      state.userSetFromTimestamp = '';
      state.visibleRunIds = [];
      state.runsById = {};
      state.fromRun = 0;
      state.totalRuns = 0;
      state.fetchedAtleastOnce = false;
    },
    setRunDetailsById: (state, action: PayloadAction<WorkflowRun>) => {
      state.runsById[action.payload.objectUUID] = {
        ...state.runsById[action.payload.objectUUID],
        state: action.payload.state,
        currentTransit: action.payload.currentTransit,
        lastUpdateTime: action.payload.lastUpdateTime,
      };
      state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
    },
    insertNodeIntoGraph: (
      state,
      action: PayloadAction<{
        parentID: string;
        name: string;
        nodeToInsert: ExistingGraphNodeItem | NewGraphNodeItem;
        module?: Module;
      }>
    ) => {
      const { nodes, start, graph } = compose(
        updateGraphFromNodesAndStart,
        insertNode
      )({
        ...action.payload,
        nodes: state.data.nodes,
        start: state.data.start,
      });
      state.data.start = start;
      state.data.nodes = nodes;
      state.data.graph = graph;
      state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
    },
    removeNodeFromGraph: (
      state,
      action: PayloadAction<{
        id: string;
        type: GraphNodeTypes;
      }>
    ) => {
      const payload = {
        ...action.payload,
        ...{ parentIDs: state.data.graph.upwardsMap[action.payload.id] },
      };
      const { nodes, start, graph } = compose(
        updateGraphFromNodesAndStart,
        removeNode
      )({
        ...payload,
        nodes: state.data.nodes,
        start: state.data.start,
        layerMap: state.data.graph.layerMap,
      });

      state.data.start = start;
      state.data.nodes = nodes;
      state.data.graph = graph;
      state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
    },
    setStateData: (
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) => {
      if (
        action.payload.name &&
        isLabelUnique(action.payload.name, state.data.nodes)
      ) {
        state.data.nodes[action.payload.id].state = action.payload.name;
        state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
      }
    },
    setChildWorkflowData: (
      state,
      action: PayloadAction<{
        id: string;
        state: string;
        childConversation: string;
        childIterator: string | number;
        childInput: any;
      }>
    ) => {
      if (
        action.payload.state &&
        isLabelUnique(action.payload.state, state.data.nodes)
      ) {
        state.data.nodes[action.payload.id].state = action.payload.state;
      }
      state.data.nodes[action.payload.id].childConversation =
        action.payload.childConversation;
      state.data.nodes[action.payload.id].childIterator =
        action.payload.childIterator;
      state.data.nodes[action.payload.id].childInput =
        action.payload.childInput;
      state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
    },
    setModuleData: (
      state,
      action: PayloadAction<{
        id: string;
        moduleData: ModuleModel<AllModuleParamModel>;
      }>
    ) => {
      state.data.nodes[action.payload.id].moduleData =
        action.payload.moduleData;
      state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
    },
    setJunctionData: (
      state,
      action: PayloadAction<{
        id: string;
        state: string;
        question: string;
        retryHelpTxt: string;
      }>
    ) => {
      if (
        action.payload.state &&
        isLabelUnique(action.payload.state, state.data.nodes)
      ) {
        state.data.nodes[action.payload.id].state = action.payload.state;
      }
      state.data.nodes[action.payload.id].question = action.payload.question;
      state.data.nodes[action.payload.id].retryHelpTxt =
        action.payload.retryHelpTxt;
      state.hasChanged = !_.isEqual(state.originalNodes, state.data.nodes);
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchWorkflowData.pending, state => {
      state.response.intent = '';
      state.response.objectUUID = '';
      state.isLoading = true;
      state.hasNetworkError = false;
    });
    builder.addCase(fetchWorkflowData.rejected, (state, action) => {
      if (sessionStorage.getItem(SESSION_WORKFLOW_KEY)) {
        switchBackToLegacyViewWithError(action.error);
      }
      state.response.intent = '';
      state.response.objectUUID = '';
      state.isLoading = false;
      state.hasNetworkError = true;
    });
    builder.addCase(fetchWorkflowData.fulfilled, (state, action) => {
      const { startState, transits, isModified } = action.payload;
      try {
        const { start, nodes } = parseWorkflow(startState, transits);

        const { graph } = updateGraphFromNodesAndStart({ nodes, start });
        state.data = {
          nodes,
          start,
          graph,
        };

        state.response = action.payload;
        state.isLoading = false;

        state.hasChanged = !!isModified;
        state.originalNodes = isModified ? {} : nodes;

        sessionStorage.removeItem(SESSION_WORKFLOW_KEY);
      } catch (error) {
        // TODO Handle/report the error
        console.log(error);
        throw error;
      }
    });

    builder.addCase(fetchWorkflowRunsList.fulfilled, (state, action) => {
      state.runsById = action.payload.data.reduce(
        (acc, run) => ({ ...acc, [run.objectUUID]: run }),
        state.runsById
      );
      state.visibleRunIds = [
        ...state.visibleRunIds,
        ...action.payload.data.map(run => run.objectUUID),
      ];
      state.totalRuns = action.payload.info.total;
      state.runsWereFound =
        action.payload.info.total > 0 || state.runsWereFound;
      state.isLoading = false;
      state.hasNetworkError = false;
      state.fetchedAtleastOnce = true;
    });
    builder.addCase(fetchWorkflowRunsList.pending, state => {
      state.isLoading = true;
      state.hasNetworkError = false;
    });
    builder.addCase(fetchWorkflowRunsList.rejected, state => {
      state.visibleRunIds = [];
      state.runsById = {};
      state.isLoading = false;
      state.hasNetworkError = true;
    });

    builder.addCase(saveWorkflowData.fulfilled, state => {
      state.isSaving = false;
      state.hasChanged = false;
      state.originalNodes = state.data.nodes;
    });
    builder.addCase(saveWorkflowData.pending, state => {
      state.isSaving = true;
    });
    builder.addCase(saveWorkflowData.rejected, state => {
      state.isSaving = false;
    });
  },
});

export const allRunsSelector = createSelector(
  (state: StoreState) => state.workflow.visibleRunIds,
  (state: StoreState) => state.workflow.runsById,
  (visibleRunIds, runsById) => visibleRunIds.map(runId => runsById[runId])
);

export const workflowUUIDSelector = (state: StoreState) =>
  state.workflow.response.objectUUID;

export const workflowNameSelector = (state: StoreState) =>
  state.workflow.response.intent;

export const workflowDataSelector = (state: StoreState) =>
  state.workflow.response;

export const graphSelector = (state: StoreState) => {
  return state.workflow.data.graph;
};

export const nodesSelector = (state: StoreState) => {
  return state.workflow.data.nodes;
};

export const startNodeSelector = (state: StoreState) => {
  return state.workflow.data.start;
};

export const userSetFromTimestampSelector = (state: StoreState) =>
  state.workflow.userSetFromTimestamp;

export const defaultFromTimestampSelector = (state: StoreState) =>
  state.workflow.defaultFromTimestamp;

export const filterSelector = (state: StoreState) => state.workflow.filter;

export const fromRunSelector = (state: StoreState) => state.workflow.fromRun;

export const totalRunsSelector = (state: StoreState) =>
  state.workflow.totalRuns;

export const runsWereFoundSelector = (state: StoreState) =>
  state.workflow.runsWereFound;

export const isWorkflowLoadingSelector = (state: StoreState) =>
  state.workflow.isLoading;

export const isWorkflowSavingSelector = (state: StoreState) =>
  state.workflow.isSaving;

export const hasChangedSelector = (state: StoreState) =>
  state.workflow.hasChanged;

export const hasNetworkErrorSelector = (state: StoreState) =>
  state.workflow.hasNetworkError;

export const fetchedAtleastOnceSelector = (state: StoreState) =>
  state.workflow.fetchedAtleastOnce;

export const parentIDSelector = (id: string) => (state: StoreState) =>
  state.workflow.data.graph.upwardsMap[id];

export const layerMapSelector = (state: StoreState) =>
  state.workflow.data.graph.layerMap;

export const {
  setFilter,
  setUserSetTimestamp,
  setDefaultFromTimestamp,
  setFromRun,
  setRunDetailsById,
  insertNodeIntoGraph,
  removeNodeFromGraph,
  setStateData,
  setChildWorkflowData,
  setModuleData,
  setJunctionData,
} = WorkflowSlice.actions;

export default WorkflowSlice.reducer;

export const insertNode = (args: {
  parentID: string;
  nodes: WorkflowState['data']['nodes'];
  start: StartNode;
  nodeToInsert: ExistingGraphNodeItem | NewGraphNodeItem;
  module?: Module;
  name: string;
}): {
  nodes: WorkflowState['data']['nodes'];
  start: WorkflowState['data']['start'];
} => {
  const nodes = { ...args.nodes };
  const parent = nodes[args.parentID];
  let children: string[] = [];
  if (args.parentID !== 'trigger') {
    if (parent.type !== GraphNodeTypes.JUNCTION) {
      children = parent.next;
    }
  } else {
    children = [args.start];
  }

  let newNode: WorkflowStateNode | undefined;
  const id = args.nodeToInsert.id;

  let start = args.start;

  switch (args.nodeToInsert.type) {
    case GraphNodeTypes.STATE:
      newNode = {
        id,
        type: GraphNodeTypes.STATE,
        state: args.name,
        next: [...children],
      };
      if (parent?.type === GraphNodeTypes.GROUP_EXIT) {
        newNode.groupExitID = args.parentID;
      }
      break;
    case GraphNodeTypes.JUNCTION: {
      const state1 = {
        id: uuid(nodes),
        type: GraphNodeTypes.STATE,
        state: `State ${generateFourDigitNumber()}`,
        next: [...children],
      };
      const state2 = {
        id: uuid(nodes),
        type: GraphNodeTypes.STATE,
        state: `State ${generateFourDigitNumber()}`,
        next: [],
      };
      newNode = {
        id,
        type: GraphNodeTypes.JUNCTION,
        state: args.name,
        next: [state1.id, state2.id],
      };
      nodes[state1.id] = state1;
      nodes[state2.id] = state2;
      if (parent?.type === GraphNodeTypes.GROUP_EXIT) {
        newNode.groupExitID = args.parentID;
      }
      break;
    }
    case GraphNodeTypes.CHILD:
      newNode = {
        id,
        type: GraphNodeTypes.CHILD,
        state: args.name,
        next: [...children],
        childConversation: args.nodeToInsert.childConversation,
        childIterator: args.nodeToInsert.childIterator,
        childInput: args.nodeToInsert.childInput,
      };
      if (parent?.type === GraphNodeTypes.GROUP_EXIT) {
        newNode.groupExitID = args.parentID;
      }
      break;
    case GraphNodeTypes.MODULE:
      newNode = {
        id,
        type: GraphNodeTypes.MODULE,
        next: [...children],
        pluginUUID: args.nodeToInsert.pluginUUID,
        moduleID: args.nodeToInsert.moduleID,
        moduleData: args.nodeToInsert.moduleData,
      };
      break;
    case GraphNodeTypes.GROUP_JUNCTION: {
      if (!args.nodeToInsert.moduleID || !args.nodeToInsert.pluginUUID)
        throw new Error('module id or plugin id not available');

      const groupName = `groupJunction${generateFourDigitNumber()}`;
      const exitIds: string[] = [];
      // TODO: Type module nodes and other nodes properly to narrow down the type here
      if (args.module && args.module.exits) {
        args.module.exits.forEach((exit, index) => {
          const id = uuid(nodes);
          nodes[id] = {
            id,
            type: GraphNodeTypes.GROUP_EXIT,
            state: `${groupName}$${exit}`,
            next: index === 0 ? children : [],
            groupName: groupName,
            exit: exit,
          };
          exitIds.push(id);
        });
      }

      const newGroupMainNode = {
        id: uuid(nodes),
        type: GraphNodeTypes.GROUP_JUNCTION,
        next: exitIds,
        groupName: groupName,
        state: `${groupName}$main`,
        isGroupMain: true,
      };

      nodes[newGroupMainNode.id] = newGroupMainNode;

      newNode = {
        id,
        type: GraphNodeTypes.GROUP_JUNCTION,
        next: exitIds,
        pluginUUID: args.nodeToInsert.pluginUUID,
        moduleID: args.nodeToInsert.moduleID,
        groupName: groupName,
        state: `${groupName}$start`,
      };
      break;
    }
    default: {
      const _exhaustiveCheck: never = args.nodeToInsert.type;
      return _exhaustiveCheck;
    }
  }

  nodes[id] = newNode;

  if (args.parentID === 'trigger') {
    start = id;
  } else {
    if (parent.type === GraphNodeTypes.JUNCTION) {
      nodes[args.parentID].next = [...parent.next, id];
    } else {
      nodes[args.parentID].next = [id];
    }
  }
  return {
    nodes,
    start,
  };
};

const traverseGraph = (
  graph: WorkflowState['data']['nodes'],
  nodeID: string,
  callback: (
    result: string[],
    node: WorkflowState['data']['nodes'][string]
  ) => void,
  ignoreFirstNode = false
) => {
  const queue = ignoreFirstNode ? [...graph[nodeID].next] : [nodeID];
  const result: string[] = [];
  const visited: Record<string, boolean> = {};
  visited[nodeID] = true;
  let currentNode;
  while (queue.length) {
    currentNode = queue.shift() as string;
    callback(result, graph[currentNode]);
    graph[currentNode].next.forEach(neighbor => {
      if (!visited[neighbor]) {
        visited[neighbor] = true;
        queue.push(neighbor);
      }
    });
  }
  return result;
};

const getRunModulesAndNextStatesForStateNode = (
  nodes: WorkflowState['data']['nodes'],
  stateID: string
): [string[], string[]] => {
  let next = nodes[stateID].next[0];
  const nextStates: string[] = [];
  const runModules: string[] = [];
  while (next && nodes[next]) {
    if (nodes[next].type === GraphNodeTypes.MODULE) {
      runModules.push(next);
    } else {
      nextStates.push(next);
      break;
    }
    next = nodes[next].next[0];
  }
  return [runModules, nextStates];
};

export const removeNode = (args: {
  parentIDs: string[];
  nodes: WorkflowState['data']['nodes'];
  start: StartNode;
  id: string;
  type: GraphNodeTypes;
  layerMap: Record<string, number>;
}): {
  nodes: WorkflowState['data']['nodes'];
  start: WorkflowState['data']['start'];
} => {
  let nodes = { ...args.nodes };
  let childIDs: string[] = [];
  switch (args.type) {
    case GraphNodeTypes.MODULE:
      childIDs = nodes[args.id].next;
      break;
    case GraphNodeTypes.CHILD:
      childIDs = nodes[args.id].next;
      break;
    case GraphNodeTypes.STATE: {
      // TODO: this can be refactored. NextState information now available in state nodes
      const [runModules, nextStates] = getRunModulesAndNextStatesForStateNode(
        nodes,
        args.id
      );
      childIDs = nextStates;
      nodes = omit(nodes, runModules);
      break;
    }
    case GraphNodeTypes.JUNCTION: {
      const nodesToDelete = traverseGraph(
        nodes,
        args.id,
        (result: string[], node: WorkflowStateNode) => {
          if (args.layerMap[node.id] > args.layerMap[args.id]) {
            result.push(node.id);
          }
        }
      );
      nodes = omit(nodes, nodesToDelete);
      break;
    }
    case GraphNodeTypes.GROUP_JUNCTION: {
      const nodesToDelete = traverseGraph(
        nodes,
        args.id,
        (result: string[], node: WorkflowStateNode) => {
          if (args.layerMap[node.id] > args.layerMap[args.id]) {
            result.push(node.id);
          }
        }
      );

      const relatedNodes = getRelatedNodesFromStartNodeForGroup(
        nodes,
        nodes[args.id].groupName || ''
      );
      if (relatedNodes) {
        nodesToDelete.push(...relatedNodes);
      }

      nodes = omit(nodes, nodesToDelete);
      break;
    }
    default:
      break;
  }

  // remove children having themselves as parents - a loop on to itself
  childIDs = without(childIDs, ...args.parentIDs);

  args.parentIDs.forEach(parent => {
    if (nodes[parent]) {
      if (nodes[parent].type === GraphNodeTypes.JUNCTION) {
        nodes[parent].next = [
          ...without(nodes[parent].next, args.id),
          ...childIDs,
        ];
      } else if (nodes[parent].type === GraphNodeTypes.GROUP_JUNCTION) {
        const groupExitID = nodes[args.id].groupExitID as string;
        if (childIDs.length === 0) {
          nodes[groupExitID].next = [];
        } else {
          nodes[groupExitID].next = [
            ...without(nodes[groupExitID].next, args.id),
            ...childIDs,
          ];
          const nextState = nodes[nodes[groupExitID].next[0]];
          nextState.groupExitID = groupExitID;
        }
      } else {
        nodes[parent].next = childIDs;
      }
    }
  });

  const start = args.start === args.id ? childIDs[0] : args.start;
  delete nodes[args.id];
  return { nodes, start };
};

const getRelatedNodesFromStartNodeForGroup = (
  nodes: any,
  groupName: string
) => {
  const relatedNodes: any[] = [];
  Object.keys(nodes).map(k => {
    if (
      groupName !== '' &&
      nodes[k].groupName === groupName &&
      nodes[k].state !== `${groupName}$start`
    ) {
      relatedNodes.push(nodes[k].id);
    }
  });
  return relatedNodes;
};

const updateGraphFromNodesAndStart = (args: ReturnType<typeof insertNode>) => {
  const graphInput: any = {};
  const { nodes, start } = args;

  const filterExitsWithChildren = (node: any, id: string) => {
    if (node.type === GraphNodeTypes.GROUP_JUNCTION) {
      if (!node.isGroupMain) {
        const children = node.next;
        const newChildren: any = [];
        _.each(children, childID => {
          if (nodes[childID].next.length === 0) {
            graphInput[childID] = nodes[childID];
            newChildren.push(childID);
          } else {
            newChildren.push(nodes[childID].next[0]);
          }
        });
        graphInput[id] = {
          ...node,
          next: newChildren,
        };
      }
    } else if (node.type !== GraphNodeTypes.GROUP_EXIT) {
      graphInput[id] = node;
    }
  };

  _.each(nodes, filterExitsWithChildren);

  // 3. produce layout with graphjs
  const graphjs = GraphJS.create({
    items: graphInput,
    start,
    parsers: [triggerParser],
    alignment: ALIGN_CENTER,
  });

  graphjs.build();

  const {
    edges,
    items,
    layerArr,
    layerMap,
    positionMap,
    upwardsMap,
    downwardsMap,
  } = graphjs.graph;

  const graph = {
    edges,
    items,
    layerArr,
    layerMap,
    positionMap,
    upwardsMap,
    downwardsMap,
  };

  return { graph, nodes, start };
};

export const createTransitMap = (nodes: WorkflowState['data']['nodes']) => {
  const newResult = transform(
    nodes,
    (result: Record<string, any>, node, id, original) => {
      // TODO: type the transit map from the response
      if (node.type !== GraphNodeTypes.MODULE && node.state) {
        result[node.state] = omit(node, [
          'id',
          'next',
          'exit',
          'type',
          'isJunctionState',
          'isGroupState',
          'isGroupMain',
          'moduleID',
          'pluginUUID',
          'moduleData',
          'isExecuted',
          'groupExitID',
        ]);
        if (node.type === GraphNodeTypes.STATE) {
          const [runModules, nextStates] =
            getRunModulesAndNextStatesForStateNode(nodes, id);
          result[node.state].runModules = runModules.map(module =>
            omit(nodes[module], ['id', 'type'])
          );
          result[node.state].nextState = nextStates.map(
            nextState => original[nextState].state
          );
        } else if (node.type === GraphNodeTypes.GROUP_JUNCTION) {
          if (node.state === `${node.groupName}$start`) {
            result[node.state].nextState = [`${node.groupName}$main`];
            result[node.state].runModules = [
              {
                moduleID: node.moduleID,
                pluginUUID: node.pluginUUID,
                moduleData: node.moduleData || {},
                groupName: node.groupName,
              },
            ];
          } else {
            result[node.state].nextState = node.next.map(
              nextState => original[nextState].state
            );
          }
        } else {
          result[node.state].runModules = node.runModules?.map(module =>
            omit(module, ['id', 'type'])
          );
          result[node.state].nextState = node.next.map(
            nextState => original[nextState].state
          );
        }
      }
    },
    {}
  );
  return newResult;
};
